import { getGroupSchedule, formatRange, getOccupiedSlots } from "./time";
import { getWarnings, hasAvailabilityConflict, getPreferredPartners } from "./validation";

// Scores how well one player fits a given group and explains why. Powers the
// "Find Player" view, which is the inverse of "Find a Time": pick a group,
// rank the whole roster by fit rather than hiding anyone.
//
// The negative signals reuse getWarnings() verbatim so this view always agrees
// with the warnings shown elsewhere (GroupModal, drag-and-drop). Positive
// signals (availability, exact-tier, preferred partners, unassigned) are added
// on top. A reason is { kind: "pos" | "neg" | "info", text }.
export function scorePlayerForGroup(player, group, allPlayers) {
  const reasons = [];
  let score = 0;

  const inGroup = group.playerIds.includes(player.id);
  const scheduled = Boolean(group.day && getOccupiedSlots(group).length > 0);

  // --- Availability (the strongest signal, and the primary ranking key) ---
  const available = scheduled ? !hasAvailabilityConflict(player, group) : null;
  if (scheduled) {
    const { startTime, duration } = getGroupSchedule(group);
    const when = `${group.day} ${startTime ? formatRange(startTime, duration) : ""}`.trim();
    if (available) {
      score += 4;
      reasons.push({ kind: "pos", text: `Free at this time (${when})` });
    } else {
      reasons.push({ kind: "neg", text: `Not free at ${when}` });
    }
  } else {
    reasons.push({ kind: "info", text: "Group has no day/time set yet" });
  }

  // --- Tier / classification ---
  const groupIsJunior = group.tier.startsWith("Junior");
  const playerIsJunior = player.classification === "Junior";
  if (player.tier === group.tier) {
    score += 3;
    reasons.push({ kind: "pos", text: `Same tier (${group.tier})` });
  } else if (groupIsJunior === playerIsJunior) {
    score += 1;
    reasons.push({ kind: "info", text: `Different tier: ${player.tier}` });
  }

  // --- Preferred partners (both directions) ---
  const memberSet = new Set(group.playerIds);
  const wants = getPreferredPartners(player, allPlayers).filter(pp => memberSet.has(pp.id));
  if (wants.length > 0) {
    score += 3;
    reasons.push({ kind: "pos", text: `Wants to play with ${wants.map(p => p.name).join(", ")} (in this group)` });
  }
  const wantedBy = group.playerIds
    .map(id => allPlayers.find(p => p.id === id))
    .filter(m => m && m.id !== player.id && getPreferredPartners(m, allPlayers).some(pp => pp.id === player.id));
  if (wantedBy.length > 0) {
    score += 2;
    reasons.push({ kind: "pos", text: `Requested by ${wantedBy.map(p => p.name).join(", ")} in this group` });
  }

  // --- Current placement ---
  if (inGroup) {
    reasons.push({ kind: "info", text: "Already in this group" });
  } else if (!player.groupId) {
    score += 1;
    reasons.push({ kind: "pos", text: "Currently unassigned" });
  } else {
    reasons.push({ kind: "info", text: `Currently in ${player.groupId}` });
  }

  // --- Authoritative negatives from the app's own rules. Skip the
  // availability warning (already represented above) and the capacity warning
  // (surfaced separately so it can also gate the Add button). ---
  const full = group.playerIds.length >= 4;
  getWarnings(player, group).forEach(w => {
    if (/did not list this slot/.test(w)) return;
    if (/already has 4 players/.test(w)) return;
    score -= 2;
    reasons.push({ kind: "neg", text: w });
  });

  return { player, score, available, inGroup, full, reasons };
}

// Ranks the whole roster for a group: best fit first, players already in the
// group sink to the bottom, ties broken by availability then name.
export function rankPlayersForGroup(group, allPlayers) {
  if (!group) return [];
  return allPlayers
    .map(p => scorePlayerForGroup(p, group, allPlayers))
    .sort((a, b) => {
      if (a.inGroup !== b.inGroup) return a.inGroup ? 1 : -1;
      if (b.score !== a.score) return b.score - a.score;
      const av = (x) => (x.available === true ? 0 : x.available === null ? 1 : 2);
      if (av(a) !== av(b)) return av(a) - av(b);
      return a.player.name.localeCompare(b.player.name);
    });
}
